import apiClient from './api';

/**
 * Spreadsheet (.xlsx) import of CPE credits. Rows become ordinary editable manual credits grouped
 * under a batch that can be undone. Design: docs/plans/excel-credit-import.md.
 */

export type ExcelColumnKind = 'core' | 'category' | 'ignored' | 'unrecognized';

export interface ExcelImportColumn {
  header: string;
  kind: ExcelColumnKind;
  /** core: email | activityTitle | dateEarned | providerName | nasbaSponsorId | deliveryMethod | notes */
  field?: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
  /** unrecognized: at least one numeric cell — a candidate field of study for the mapping strip */
  hasNumbers: boolean;
}

export interface ExcelImportIssue {
  /** core field, 'hours', 'hours:{categoryId}', 'unmapped:{header}' or 'row' */
  field: string;
  message: string;
}

export interface ExcelImportRow {
  rowNumber: number;
  email?: string | null;
  userId?: string | null;
  activityTitle?: string | null;
  /** yyyy-MM-dd when parsed */
  dateEarned?: string | null;
  /** original text when the date could not be parsed */
  dateEarnedRaw?: string | null;
  providerName?: string | null;
  nasbaSponsorId?: string | null;
  deliveryMethod?: string | null;
  notes?: string | null;
  /** categoryId → hours */
  hours: Record<string, number>;
  /** unrecognized header → hours */
  unmappedHours: Record<string, number>;
  isDuplicate: boolean;
  issues: ExcelImportIssue[];
}

export interface ExcelImportCategory {
  id: number;
  name: string;
}

/** Sheet rows the parser did not treat as activities (a name line, a requirements table, totals), reported so nothing is dropped silently. */
export interface ExcelImportSkippedRange {
  fromRow: number;
  toRow: number;
  /** 'above the header' | 'after the table' | 'no title or date' */
  reason: string;
}

export interface ExcelImportPreview {
  batchId: string;
  fileName: string;
  sheetName: string;
  /** 1-based sheet row the header was found on. */
  headerRow?: number;
  skipped?: ExcelImportSkippedRange[];
  /** How the file was read: 'template' | 'lcvista' | 'other'. Detection wins over the chosen source unless forced. */
  source: string;
  /** What the parser recognized, or null when nothing matched (or the choice was forced). */
  detectedSource?: string | null;
  /** Firm mode, per-person export with no Email column: the email found above the header, applied to every row. */
  preambleEmail?: string | null;
  mode: 'self' | 'tenant';
  tenantId?: string | null;
  columns: ExcelImportColumn[];
  categories: ExcelImportCategory[];
  deliveryMethods: string[];
  rows: ExcelImportRow[];
  ready: number;
  duplicates: number;
  errors: number;
}

export interface ExcelImportCommitRow {
  rowNumber: number;
  email?: string | null;
  activityTitle?: string | null;
  dateEarned?: string | null;
  providerName?: string | null;
  nasbaSponsorId?: string | null;
  deliveryMethod?: string | null;
  notes?: string | null;
  hours: Record<string, number>;
  unmappedHours?: Record<string, number>;
}

export interface ExcelImportCommit {
  batchId: string;
  tenantId?: string | null;
  fileName: string;
  sheetName?: string | null;
  /** The preview's source, recorded on the batch. */
  source?: string | null;
  /** unrecognized header → categoryId */
  columnMapping?: Record<string, number>;
  rows: ExcelImportCommitRow[];
  rowsSkipped: number;
}

export interface CreditImportBatch {
  id: string;
  fileName: string;
  sheetName?: string | null;
  /** 'template' | 'lcvista' | 'other'; null for batches from before sources were tracked. */
  source?: string | null;
  uploadedAt: string;
  uploadedByUserId: string;
  uploadedByName: string;
  uploadedBySelf: boolean;
  targetUserId?: string | null;
  targetUserName?: string | null;
  rowsImported: number;
  rowsSkipped: number;
  undoneAt?: string | null;
  canUndo: boolean;
  editedRows: number;
}

export interface CreditImportUndoResult {
  batchId: string;
  rowsRemoved: number;
  undoneAt: string;
}

/** Firm mode: the user an edited email now points at, or null when nobody in the firm has it. */
export interface ExcelImportEmailResolution {
  email: string;
  userId?: string | null;
}

/** Shape of the 400 body when commit is refused (strict mode) or the file is rejected. */
export interface CreditImportErrorBody {
  message?: string;
  rows?: { rowNumber: number; issues: ExcelImportIssue[] }[];
  editedRows?: number;
}

const BASE = '/api/cpe-history/imports/excel';

function withTenant(path: string, tenantId?: string | null): string {
  return tenantId ? `${path}?tenantId=${encodeURIComponent(tenantId)}` : path;
}

class CreditImportService {
  /**
   * Parse and validate a workbook. Nothing is written. `source` is the profile the user picked;
   * the server's detection wins unless `force` is set (the review's "read as a spreadsheet instead").
   */
  async parse(file: File, tenantId?: string | null, options: { source?: string; force?: boolean } = {}): Promise<ExcelImportPreview> {
    const formData = new FormData();
    formData.append('file', file);
    if (options.source) formData.append('source', options.source);
    if (options.force) formData.append('force', 'true');
    const response = await apiClient.post<ExcelImportPreview>(withTenant(`${BASE}/parse`, tenantId), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /** Import the reviewed rows. Strict: the server re-validates and refuses with per-row issues. Idempotent on batchId. */
  async commit(commit: ExcelImportCommit): Promise<CreditImportBatch> {
    const response = await apiClient.post<CreditImportBatch>(`${BASE}/commit`, commit);
    return response.data;
  }

  async listBatches(tenantId?: string | null): Promise<CreditImportBatch[]> {
    const response = await apiClient.get<CreditImportBatch[]>(withTenant(`${BASE}/batches`, tenantId));
    return response.data;
  }

  /** Firm mode: re-match emails the admin corrected in the review. Unknown and other-firm emails both come back without a user. */
  async resolveEmails(tenantId: string, emails: string[]): Promise<ExcelImportEmailResolution[]> {
    const response = await apiClient.post<ExcelImportEmailResolution[]>(`${BASE}/resolve-emails`, { tenantId, emails });
    return response.data;
  }

  /** Remove every credit the batch created. Rejects with 409 when any was edited since. */
  async undo(batchId: string): Promise<CreditImportUndoResult> {
    const response = await apiClient.post<CreditImportUndoResult>(`${BASE}/batches/${batchId}/undo`);
    return response.data;
  }

  /** Download the empty template as a file. */
  async downloadTemplate(tenantId?: string | null): Promise<void> {
    const response = await apiClient.get<Blob>(withTenant(`${BASE}/template`, tenantId), { responseType: 'blob' });
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'cpe-credits-template.xlsx';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

export default new CreditImportService();
